import {
  addUser,
  getUserCoursesInGroup,
  getUserBundlesInGroup,
} from "../../../service/GroupService";
import { Form, Input, DatePicker, Typography, Spin } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import InputWrapper from "../../../components/shared/Input/Input-Wrapper";
import CustomModal from "../../../components/global/CustomModal/CustomModal";
import CustomLabelDropdown from "../../../components/global/CustomLabelDropdown/CustomLabelDropdown";
import CustomButton from "../../../components/global/Button/CustomButton";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../components/shared/pagination/messages";

const { Text } = Typography;
const AllocateCourseModal = ({
  isModalOpen,
  groupId,
  userId,
  getUsers,
  getCourses,
  getBundles,
  handleCloseModal,
  setLoading,
  loading,
}) => {
  const [form] = Form.useForm();
  const [searchCourse, setSearchCourse] = useState("");
  const [courses, setCourses] = useState([]);
  const [courseMsg, setCourseMsg] = useState("");
  const [bundles, setBundles] = useState([]);
  const [bundleMsg, setBundleMsg] = useState("");

  const filteredCourses = courses?.filter((user) =>
    user.title.toLowerCase().includes(searchCourse.toLowerCase()),
  );

  const filteredBundles = bundles?.filter((user) =>
    user.title.toLowerCase().includes(searchCourse.toLowerCase()),
  );

  async function getCourseList() {
    const payload = {
      groupId: Number(groupId),
      userId: Number(userId),
    };

    const result = await getUserCoursesInGroup(payload);

    if (result?.success) {
      const courses = result.data?.data?.map((user) => ({
        courseId: user.courseId,
        title: user.title,
        level: user.courseLevel,
      }));
      setCourses(courses);
      setCourseMsg(result.data?.message);
    } else {
      console.error("Error fetching course list:", result?.data);
      setCourses([]);
    }
  }

  async function getBundleList() {
    const payload = {
      groupId: Number(groupId),
      userId: Number(userId),
    };

    const result = await getUserBundlesInGroup(payload);

    if (result?.success) {
      const bundles = result.data?.data?.map((user) => ({
        bundleId: user.bundleId,
        title: user.bundleName,
      }));
      setBundles(bundles);
      setBundleMsg(result.data?.message);
    } else {
      console.error("Error fetching bundle list:", result?.data);
      setBundles([]);
    }
  }

  useEffect(() => {
    if (!isModalOpen) return;

    form.setFieldsValue({
      groupId: groupId,
      employees: [userId],
      courses: [],
      bundles: [],
    });

    getCourseList();
    getBundleList();
  }, [isModalOpen, userId]);

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();

      const hasCourses = values.courses?.length > 0;
      const hasBundles = values.bundles?.length > 0;

      if (!hasCourses && !hasBundles) {
        showErrorMessage(
          "Please select at least one Course or Curriculum to continue.",
        );
        return;
      }

      const payload = {
        groupId: Number(values.groupId),
        employees: [userId],
        courses: values.courses,
        deadline: values.deadline,
        bundles: values.bundles,
        assignedAt: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
      };

      setLoading(true);
      const result = await addUser(payload);

      if (result?.success) {
        showSuccessMessage("Course allocated successfully!");
        getUsers();
        getCourses();
        getBundles();
        handleCloseModal();
      } else {
        showErrorMessage(
          result?.data || "Error occurred while allocating course.",
        );
      }
    } catch (error) {
      if (error?.errorFields) return;
      showErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const selectedCourses = Form.useWatch("courses", form);
  const selectedBundles = Form.useWatch("bundles", form);
  return courses?.length > 0 || bundles?.length > 0 ? (
    <CustomModal
      title={`Allocate Course/Curriculum`}
      visible={isModalOpen}
      onCancel={handleCloseModal}
    >
      <Form
        form={form}
        layout="vertical"
        name="group_form"
        className="flex flex-col gap-4"
      >
        <Form.Item name="groupId" noStyle>
          <Input type="hidden" />
        </Form.Item>

        <InputWrapper label="Select Courses" >
          <Form.Item name="courses">
            <CustomLabelDropdown
              placeholder="Select course"
              showSearch
              mode="multiple"
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = option?.label || "";
                return (
                  String(label).toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              maxTagCount="responsive"
              notFoundContent={
                loading ? <Spin size="small" /> : "No courses available"
              }
              options={filteredCourses?.map((course) => ({
                value: course.courseId,
                label: course.title,
              }))}
              wrapperClass="course-multi-selector"
            />
          </Form.Item>
        </InputWrapper>

        <InputWrapper label="Select Curriculums" >
          <Form.Item name="bundles">
            <CustomLabelDropdown
              placeholder="Select curriculum"
              showSearch
              mode="multiple"
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = option?.label || "";
                return (
                  String(label).toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              maxTagCount="responsive"
              notFoundContent={
                loading ? <Spin size="small" /> : "No bundles available"
              }
              options={filteredBundles?.map((bundle) => ({
                value: bundle.bundleId,
                label: bundle.title,
              }))}
              wrapperClass="bundle-multi-selector"
            />
          </Form.Item>
        </InputWrapper>

        {(selectedCourses?.length > 0 || selectedBundles?.length > 0) && (
          <InputWrapper label={"Deadline"} required>
            <Form.Item
              name="deadline"
              rules={[{ required: true, message: "Please select a deadline!" }]}
            >
              <DatePicker
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  height: "46px",
                  marginBottom: "10px",
                }}
              />
            </Form.Item>
          </InputWrapper>
        )}
      </Form>
      <div className="flex justify-center items-center gap-[10px] w-full mt-4">
        <CustomButton className="!w-1/2" variant="primary" onClick={handleAdd}>
          {"Add"}
        </CustomButton>

        <CustomButton
          className="!w-1/2"
          variant="outlined"
          onClick={handleCloseModal}
        >
          Cancel
        </CustomButton>
      </div>
    </CustomModal>
  ) : (
    <CustomModal
      title={`Allocate course to user`}
      visible={isModalOpen}
      onCancel={handleCloseModal}
      width={450}
    >
      <Text>{courseMsg}</Text>
      <div className="flex justify-center items-center gap-[10px] w-full mt-4">
        <CustomButton
          className="min-w-40"
          onClick={handleCloseModal}
          variant="primary"
        >
          OK
        </CustomButton>
      </div>
    </CustomModal>
  );
};
export default AllocateCourseModal;
